import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { banners, comics } from '@/mocks/comics';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const totalSlides = banners.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goNext = useCallback(() => {
    goTo(current + 1);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  const banner = banners[current];
  const comic = comics.find((c) => c.id === banner.comicId);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      {banners.map((b, idx) => (
        <div
          key={b.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={b.image}
            alt={b.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-semibold mb-3 md:mb-4">
              <i className="ri-fire-line text-sm"></i>
              热门推荐
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 leading-tight">
              {banner.title}
            </h1>
            <p className="text-sm md:text-lg text-white/80 mb-1 md:mb-2">
              {banner.subtitle}
            </p>
            <p className="text-xs md:text-sm text-white/60 mb-4 md:mb-6">
              {banner.description}
            </p>
            {comic && (
              <Link
                to={`/article/${comic.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-primary-500 text-background-50 font-semibold text-sm md:text-base rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                立即阅读
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={() => goTo(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors duration-200"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors duration-200"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>

      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 ${
              idx === current
                ? 'bg-accent-500 w-6 md:w-7'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`第 ${idx + 1} 张`}
          ></button>
        ))}
      </div>
    </section>
  );
}